import type { SalesMoveBucket, SalesMovesSummary } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import { formatMoney } from "./format";

export type SummaryStripKey = "moves" | "replies" | "risk" | "exposure";

type Props = {
  summary: SalesMovesSummary;
  activeFilter?: SummaryStripKey | null;
  onFilter?: (
    filter: SummaryStripKey | null,
    bucket?: SalesMoveBucket | "ALL_ACTIVE",
  ) => void;
  /** Navigate out for metrics that deserve a deeper surface. */
  onNavigate?: (key: SummaryStripKey) => void;
};

export function SalesSummaryStrip({
  summary,
  activeFilter,
  onFilter,
  onNavigate,
}: Props) {
  const items = [
    {
      key: "moves" as const,
      label: "Moves today",
      value: String(summary.movesToday),
      bucket: "ALL_ACTIVE" as const,
      navigate: false,
    },
    {
      key: "replies" as const,
      label: "Replies needed",
      value: String(summary.repliesNeeded),
      bucket: "REPLY_NEEDED" as const,
      navigate: false,
    },
    {
      key: "risk" as const,
      label: "Deals at risk",
      value: String(summary.dealsAtRisk),
      bucket: "AT_RISK" as const,
      navigate: true,
    },
    {
      key: "exposure" as const,
      label: "Pipeline exposure",
      value: formatMoney(
        summary.pipelineExposure.amount,
        summary.pipelineExposure.currency,
      ),
      bucket: "ALL_ACTIVE" as const,
      navigate: true,
    },
  ];

  return (
    <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
      {items.map((item) => {
        const active = activeFilter === item.key;
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => {
              if (item.navigate && onNavigate) {
                onNavigate(item.key);
                return;
              }
              onFilter?.(
                active ? null : item.key,
                active ? undefined : item.bucket,
              );
            }}
            className={cn(
              "rounded-md border px-3 py-2.5 text-left transition",
              active
                ? "border-brand/40 bg-brand/5"
                : "border-border/80 bg-card/40 hover:bg-muted/20",
            )}
          >
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {item.label}
            </p>
            <p className="mt-0.5 text-lg font-semibold text-foreground">
              {item.value}
            </p>
            {item.navigate ? (
              <p className="mt-1 text-[10px] text-brand">Explore details</p>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
